import { toast } from 'sonner';

export interface StorageInfo {
  isDesktopStorageActive: boolean;
  usage?: {
    used: number;
    quota: number;
    percentage: number;
  };
  capabilities: {
    canWrite: boolean;
    canRead: boolean;
    hasQuotaAPI: boolean;
  };
  metrics?: StorageMetrics;
}

export interface StorageMetrics {
  totalKeys: number;
  totalSize: number;
  largestKey: string;
  largestSize: number;
  compressionOpportunities: string[];
  usedSpace: number;
  availableSpace: number;
  compressionRatio?: number;
}

export class UniversalStorage {
  private isDesktop = false;
  private storageCache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 30000; // 30 seconds

  constructor() {
    this.detectStorageMode();
  }

  private detectStorageMode(): void {
    try {
      // Always use localStorage for this implementation
      this.isDesktop = false;
      console.log('🏠 Using localStorage storage mode');
    } catch (error) {
      console.error('Storage detection failed:', error);
      this.isDesktop = false;
    }
  }

  getCurrentStorageType(): string {
    return this.isDesktop ? 'desktop' : 'localStorage';
  }

  async getAllKeys(): Promise<string[]> {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('Error getting all keys:', error);
      return [];
    }
  }

  async estimateStorageCapacity(): Promise<{ used: number; available: number; total: number; maxCapacity: number; storageUsed: string }> {
    try {
      let totalSize = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += value.length;
          }
        }
      }

      // Estimate 5MB quota for localStorage (conservative)
      const estimatedQuota = 5 * 1024 * 1024;
      const available = Math.max(0, estimatedQuota - totalSize);

      return {
        used: totalSize,
        available,
        total: estimatedQuota,
        maxCapacity: Math.floor(available / 1000), // Estimated max items
        storageUsed: `${(totalSize / 1024).toFixed(2)} KB`
      };
    } catch (error) {
      console.error('Error estimating storage capacity:', error);
      return { 
        used: 0, 
        available: 5242880, 
        total: 5242880,
        maxCapacity: 5000,
        storageUsed: '0 KB'
      };
    }
  }

  isDesktopStorageActive(): boolean {
    return this.isDesktop;
  }

  async getItem<T>(key: string): Promise<T | null> {
    try {
      // Check cache first
      const cached = this.storageCache.get(key);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      const item = localStorage.getItem(key);
      if (item === null) return null;

      const parsed = JSON.parse(item);
      
      // Update cache
      this.storageCache.set(key, { data: parsed, timestamp: Date.now() });
      
      return parsed;
    } catch (error) {
      console.error(`Error getting item ${key}:`, error);
      return null;
    }
  }

  async setItem<T>(key: string, value: T): Promise<void> {
    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        const serializedValue = JSON.stringify(value);
        
        // Check if we're approaching quota limits
        if (this.isNearQuotaLimit(serializedValue)) {
          console.log('📦 Approaching storage quota, applying optimizations...');
          await this.optimizeStorage();
        }

        localStorage.setItem(key, serializedValue);
        console.log(`✅ Successfully saved ${key} (${(serializedValue.length / 1024).toFixed(2)} KB)`);
        
        // Update cache
        this.storageCache.set(key, { data: value, timestamp: Date.now() });
        return;

      } catch (error) {
        attempt++;
        
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          console.log(`🔧 Quota exceeded on attempt ${attempt}, optimizing storage...`);
          
          if (attempt < maxRetries) {
            await this.handleQuotaExceeded(key);
            // Wait a bit before retrying
            await new Promise(resolve => setTimeout(resolve, 100));
            continue;
          } else {
            // Final attempt with emergency cleanup
            console.log('🚨 Final attempt with emergency storage cleanup...');
            await this.emergencyCleanup();
            
            try {
              localStorage.setItem(key, JSON.stringify(value));
              console.log('✅ Emergency save successful');
              this.storageCache.set(key, { data: value, timestamp: Date.now() });
              return;
            } catch (finalError) {
              throw new Error('Storage quota exceeded even after optimization. Please export your data and clear some space.');
            }
          }
        } else {
          throw error;
        }
      }
    }

    throw new Error(`Failed to save ${key} after ${maxRetries} attempts`);
  }

  private isNearQuotaLimit(newData: string): boolean {
    try {
      // Estimate current storage usage
      let totalSize = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          totalSize += localStorage.getItem(key)?.length || 0;
        }
      }
      
      const newDataSize = newData.length;
      const estimatedTotal = totalSize + newDataSize;
      
      // Assume 5MB quota limit (conservative estimate)
      const quotaLimit = 5 * 1024 * 1024;
      
      return estimatedTotal > quotaLimit * 0.8; // 80% threshold
    } catch (error) {
      console.warn('Could not estimate quota usage:', error);
      return false;
    }
  }

  private async handleQuotaExceeded(key: string): Promise<void> {
    console.log('🔧 Handling quota exceeded error with optimizations...');
    
    try {
      // 1. Clear temporary and cache data
      const keysToCheck = [];
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey) {
          keysToCheck.push(storageKey);
        }
      }
      
      // Remove temporary keys
      const tempKeys = keysToCheck.filter(k => 
        k.includes('temp_') || 
        k.includes('cache_') || 
        k.includes('_backup_temp') ||
        k.startsWith('nalc_temp')
      );
      
      tempKeys.forEach(tempKey => {
        localStorage.removeItem(tempKey);
        console.log(`🗑️ Removed temporary key: ${tempKey}`);
      });
      
      // 2. Compress existing data if not already compressed
      await this.compressExistingData();
      
      // 3. Remove old backup files (keep only latest 3)
      await this.cleanupOldBackups();
      
      console.log('✅ Quota exceeded handling completed');
      
    } catch (error) {
      console.error('Error handling quota exceeded:', error);
    }
  }

  private async emergencyCleanup(): Promise<void> {
    console.log('🚨 Performing emergency storage cleanup...');
    
    try {
      // Remove all non-essential data
      const keysToKeep = [
        'nalc_students',
        'nalc_teachers', 
        'nalc_admins',
        'nalc_app_settings'
      ];
      
      const allKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          allKeys.push(key);
        }
      }
      
      allKeys.forEach(key => {
        if (!keysToKeep.includes(key) && !key.startsWith('nalc_essential_')) {
          localStorage.removeItem(key);
          console.log(`🗑️ Emergency cleanup removed: ${key}`);
        }
      });
      
      console.log('✅ Emergency cleanup completed');
      
    } catch (error) {
      console.error('Emergency cleanup failed:', error);
    }
  }

  private async compressExistingData(): Promise<void> {
    console.log('📦 Compressing existing data to free space...');
    
    try {
      const keysToCompress = ['nalc_students', 'nalc_teachers', 'nalc_admins'];
      
      for (const key of keysToCompress) {
        const data = localStorage.getItem(key);
        if (data && data.length > 1000) { // Only compress if data is substantial
          try {
            const parsed = JSON.parse(data);
            // Simple compression by removing whitespace
            const compressed = JSON.stringify(parsed);
            if (compressed.length < data.length) {
              localStorage.setItem(key, compressed);
              console.log(`📦 Compressed ${key}: ${data.length} → ${compressed.length} bytes`);
            }
          } catch (parseError) {
            console.warn(`Could not compress ${key}:`, parseError);
          }
        }
      }
      
    } catch (error) {
      console.error('Error compressing existing data:', error);
    }
  }

  private async cleanupOldBackups(): Promise<void> {
    console.log('🗑️ Cleaning up old backup files...');
    
    try {
      const backupKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('backup') || key.includes('_bak'))) {
          backupKeys.push(key);
        }
      }
      
      // Sort by key (assuming timestamp-based keys) and keep only latest 3
      backupKeys.sort().reverse();
      const keysToRemove = backupKeys.slice(3);
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log(`🗑️ Removed old backup: ${key}`);
      });
      
    } catch (error) {
      console.error('Error cleaning up old backups:', error);
    }
  }

  private async optimizeStorage(): Promise<void> {
    console.log('🔧 Optimizing storage for better capacity...');
    
    await Promise.all([
      this.compressExistingData(),
      this.cleanupOldBackups()
    ]);
    
    console.log('✅ Storage optimization completed');
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
      this.storageCache.delete(key);
    } catch (error) {
      console.error(`Error removing item ${key}:`, error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.clear();
      this.storageCache.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
      throw error;
    }
  }

  async getStorageInfo(): Promise<StorageInfo> {
    try {
      let usage;
      
      // Try to get quota information
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          const estimate = await navigator.storage.estimate();
          usage = {
            used: estimate.usage || 0,
            quota: estimate.quota || 0,
            percentage: estimate.quota ? Math.round((estimate.usage || 0) / estimate.quota * 100) : 0
          };
        } catch (error) {
          console.warn('Could not get storage estimate:', error);
        }
      }

      const metrics = await this.getStorageMetrics();

      return {
        isDesktopStorageActive: this.isDesktop,
        usage,
        capabilities: {
          canWrite: true,
          canRead: true,
          hasQuotaAPI: 'storage' in navigator && 'estimate' in navigator.storage
        },
        metrics
      };
    } catch (error) {
      console.error('Error getting storage info:', error);
      return {
        isDesktopStorageActive: false,
        capabilities: {
          canWrite: false,
          canRead: false,
          hasQuotaAPI: false
        }
      };
    }
  }

  async getStorageMetrics(): Promise<StorageMetrics> {
    try {
      let totalKeys = 0;
      let totalSize = 0;
      let largestKey = '';
      let largestSize = 0;
      const compressionOpportunities: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          totalKeys++;
          const value = localStorage.getItem(key);
          if (value) {
            const size = value.length;
            totalSize += size;
            
            if (size > largestSize) {
              largestSize = size;
              largestKey = key;
            }
            
            // Check for compression opportunities
            if (size > 1000 && !key.includes('compressed')) {
              compressionOpportunities.push(key);
            }
          }
        }
      }

      // Estimate available space
      const estimatedQuota = 5 * 1024 * 1024; // 5MB
      const availableSpace = Math.max(0, estimatedQuota - totalSize);

      return {
        totalKeys,
        totalSize,
        largestKey,
        largestSize,
        compressionOpportunities,
        usedSpace: totalSize,
        availableSpace,
        compressionRatio: totalSize > 0 ? totalSize / estimatedQuota : 0
      };
    } catch (error) {
      console.error('Error getting storage metrics:', error);
      return {
        totalKeys: 0,
        totalSize: 0,
        largestKey: '',
        largestSize: 0,
        compressionOpportunities: [],
        usedSpace: 0,
        availableSpace: 5242880,
        compressionRatio: 0
      };
    }
  }

  clearCache(): void {
    this.storageCache.clear();
  }
}

export const universalStorage = new UniversalStorage();
